import queryString from "query-string";
import AxiosInstance from "@/config/AxiosInstance";
import {
  ENV,
  API_BASE_URL_ADMIN,
  API_BASE_URL_REPORT,
  API_BASE_URL,
} from "@/config/appConfig";
import { getLocal } from "@/helpers/localStore";
import { LOCAL_STORAGE } from "@/config/constants";

type RequestData = Record<string, any>;

type AxiosLikeResponse<T = any> = {
  data: T;
  headers?: Record<string, unknown>;
};

export const campaignService = {
  getCampaign,
  joinCampaign,
  deleteJoinCampaignLocation,
  updateJoinCampaignLocation,
  getCampaignReport,
  sendCreativeFeedback,
  getSingleCampaign,
  joinPostCardCampaign,
  getPostcardCampaign,
  postcardAddressUpload,
  gePostcardCampaignData,
  advanceSettingAttachmentUpload,
  getCampaignReportListing,
  getDigitalCampaigns,
  postcardReportListing,
  launchPostcardCampaigns,
  getCampaignReportCsv,
  getUnlinkCampaignReportCsv,
  getAllCampaignBoostLocations,
  getCurrentBudgetCalculation,
  saveCampaignBoostBudget,
  getJoinedLocations,
  requestPauseCampaign,
  getIsThereBudgetAllocationInProcessForCamp,
  getBudgetRecommendation,
  getBulkBudgetRecommendation,
  getBulkBudgetRecommendationProgressUrl,
  streamBulkBudgetRecommendationProgress,
  getCampaignExtensionLocations,
  saveCampaignExtensionBudget,
  getTagsAndChannelsForParticipant,
  getPostCardCampaignTagsParticipant,
};

function getSingleCampaign(data: RequestData) {
  const getData = {
    id: "",
    status: 1,
    row: 50,
    page: 1,
    is_deleted: 0,
    ...data,
  };
  const query = queryString.stringify(getData);
  return AxiosInstance.get(`${API_BASE_URL_ADMIN}/campaign?${query}`);
}

function getPostcardCampaign(data: RequestData) {
  const getData = {
    id: "",
    status: 1,
    row: 50,
    page: 1,
    is_deleted: 0,
    ...data,
  };
  const query = queryString.stringify(getData);
  return AxiosInstance.get(`${API_BASE_URL_ADMIN}/postcardCampaign?${query}`);
}

function gePostcardCampaignData(data: RequestData) {
  const query = queryString.stringify(data);
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/postcardCampaignData?${query}`
  );
}

function getCampaign(data: RequestData, isSWR = false) {
  const query = queryString.stringify(data);
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/campaignData?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(`${API_BASE_URL_ADMIN}/campaignData?${query}`);
}

function joinCampaign(data: RequestData) {
  return AxiosInstance.post(`${API_BASE_URL_ADMIN}/campaignParticipant`, data);
}

function deleteJoinCampaignLocation(data: RequestData) {
  return AxiosInstance.post(
    `${API_BASE_URL_ADMIN}/deleteJoinCampaignLocation`,
    data
  );
}

function updateJoinCampaignLocation(data: RequestData) {
  return AxiosInstance.post(
    `${API_BASE_URL_ADMIN}/updateJoinCampaignLocation`,
    data
  );
}

function joinPostCardCampaign(data: RequestData) {
  return AxiosInstance.post(
    `${API_BASE_URL_ADMIN}/postcardCampaignParticipant`,
    data
  );
}

function getCampaignReport(data: RequestData) {
  const getData = {
    campaign_id: "",
    location_id: "",
    start_date: "",
    end_date: "",
    group_id: "",
    channel_id: "",
    downloadReport: false,
    ...data,
  };
  const query = queryString.stringify(getData);
  return AxiosInstance.get(`${API_BASE_URL_ADMIN}/campaignReport?${query}`);
}

function getCampaignReportCsv(data: RequestData) {
  const query = queryString.stringify({
    campaign_id: "",
    location_id: "",
    group_id: "",
    channel_id: "",
    downloadReport: false,
    ...data,
  });
  return AxiosInstance.get(
    `${API_BASE_URL_REPORT}/downloadCampaignReportCsv?${query}`,
    {
      responseType: "blob",
    }
  ).then(({ data, headers }: AxiosLikeResponse) => ({ data, headers }));
}

function getUnlinkCampaignReportCsv(data: string) {
  return AxiosInstance.get(
    `${API_BASE_URL_REPORT}/downloadCampaignReportCsvUnlink?path=${data}`
  );
}

function sendCreativeFeedback(data: RequestData) {
  return AxiosInstance.post(`${API_BASE_URL_ADMIN}/feedback`, data);
}

function postcardAddressUpload(data: FormData | RequestData) {
  return AxiosInstance.post(
    `${API_BASE_URL_ADMIN}/postcardAddressUpload`,
    data
  );
}

function advanceSettingAttachmentUpload(data: FormData | RequestData) {
  return AxiosInstance.post(
    `${API_BASE_URL_ADMIN}/advanceSetting/attachmentUpload`,
    data
  );
}

function getCampaignReportListing(data: RequestData, isSWR = false) {
  const query = queryString.stringify(data);
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/campaignReportListing?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/campaignReportListing?${query}`
  );
}

function getDigitalCampaigns(data: RequestData, isSWR = false) {
  const query = queryString.stringify(data);
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/launchCampaignReportListing?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/launchCampaignReportListing?${query}`
  );
}

function postcardReportListing(data: RequestData, isSWR = false) {
  const query = queryString.stringify(data);
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/postcardReportListing?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/postcardReportListing?${query}`
  );
}

function launchPostcardCampaigns(data: RequestData, isSWR = false) {
  const query = queryString.stringify(data);
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/launchPostcardReportListing?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/launchPostcardReportListing?${query}`
  );
}

function getAllCampaignBoostLocations(data: RequestData, isSWR = false) {
  const query = queryString.stringify(data);
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/getAllCampaignBoostLocations?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/getAllCampaignBoostLocations?${query}`
  );
}

function getCurrentBudgetCalculation(data: RequestData) {
  return AxiosInstance.post(
    `${API_BASE_URL_ADMIN}/getCurrentBudgetCalculation`,
    data
  );
}

function saveCampaignBoostBudget(data: RequestData) {
  return AxiosInstance.post(
    `${API_BASE_URL_ADMIN}/saveCapmaignBoostBudget `,
    data
  );
}

function getJoinedLocations(data: RequestData) {
  return AxiosInstance.post(`${API_BASE_URL_ADMIN}/getJoinedLocations`, data);
}

function requestPauseCampaign(data: RequestData) {
  return AxiosInstance.post(`${API_BASE_URL_ADMIN}/requestPauseCampaign`, {
    campaign_id: data?.campaign_id || "",
    location_ids: data?.location_ids || [],
    ...data,
  });
}

function getIsThereBudgetAllocationInProcessForCamp(data: RequestData) {
  return AxiosInstance.post(`${API_BASE_URL}/preventJoinNowCampaign`, data);
}

function getBudgetRecommendation(data) {
  return AxiosInstance.post(`${API_BASE_URL}/budget-engine/recommend`, data);
}

function getBulkBudgetRecommendation(data, config = {}) {
  return AxiosInstance.post(
    `${API_BASE_URL}/budget-engine/recommend/bulk`,
    data,
    config
  );
}

function getBulkBudgetRecommendationProgressUrl(jobId) {
  return `${API_BASE_URL}/budget-engine/recommend/bulk/${jobId}/progress`;
}

function getApiHeaders() {
  const headers: Record<string, string> = {
    "x-caller-url": `${window.location.origin}${window.location.pathname}`,
  };
  const token = getLocal(LOCAL_STORAGE.TOKEN_NAME);

  if (ENV.PUBLIC_API_CLIENT_KEY) {
    headers.apiAccessPrivateKey = ENV.PUBLIC_API_CLIENT_KEY;
  }

  if (token) {
    headers.authorization = `Bearer ${token}`;
  }

  return headers;
}

function getSseEventData(eventBlock) {
  return eventBlock
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.replace(/^data:\s?/, ""))
    .join("\n");
}

async function streamBulkBudgetRecommendationProgress(
  jobId,
  {
    signal,
    onMessage,
    onComplete,
  }: {
    signal?: AbortSignal;
    onMessage?: (_payload: Record<string, any>) => void;
    onComplete?: (_payload: Record<string, any>) => void;
  } = {}
) {
  const response = await fetch(getBulkBudgetRecommendationProgressUrl(jobId), {
    headers: getApiHeaders(),
    signal,
  });

  if (!response.ok) {
    let message = `Budget recommendation progress failed with status ${response.status}.`;

    try {
      const errorPayload = await response.json();
      message = errorPayload?.message || message;
    } catch {
      // Keep the status-based message when the error body is not JSON.
    }

    throw new Error(message);
  }

  const reader = response.body?.getReader();

  if (!reader) {
    throw new Error("Budget recommendation progress stream is not available.");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();

    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const eventBlocks = buffer.split(/\n\n|\r\n\r\n/);
    buffer = eventBlocks.pop() || "";

    for (const eventBlock of eventBlocks) {
      const eventData = getSseEventData(eventBlock);

      if (!eventData) {
        continue;
      }

      const payload = JSON.parse(eventData);
      onMessage?.(payload);

      if (payload?.status === "complete") {
        onComplete?.(payload);
        reader.cancel();
        return;
      }
    }
  }
}

function getCampaignExtensionLocations(data: RequestData, isSWR = false) {
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL}/campaign-extension-locations/${data.campaign_id}/${data.participant_id}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL}/campaign-extension-locations/${data.campaign_id}/${data.participant_id}`
  );
}

function saveCampaignExtensionBudget(data: RequestData) {
  return AxiosInstance.post(
    `${API_BASE_URL}/campaign-extension-budget-save`,
    data
  );
}

function getTagsAndChannelsForParticipant(data: RequestData, isSWR = false) {
  const query = queryString.stringify(data);
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/tags-and-channels-for-participant?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(
    `${API_BASE_URL_ADMIN}/tags-and-channels-for-participant?${query}`
  );
}

function getPostCardCampaignTagsParticipant(data: RequestData, isSWR = false) {
  const query = queryString.stringify(data);
  if (isSWR) {
    return AxiosInstance.get(
      `${API_BASE_URL_ADMIN}/postcard-tags?${query}`
    ).then((r) => r.data);
  }
  return AxiosInstance.get(`${API_BASE_URL_ADMIN}/postcard-tags?${query}`);
}
